/**
 * Cue mapping (story `q-demo-vfx-pass`): a game-feedback event → the bursts it plays and
 * where. Pure data + one pure function, so the whole mapping is pinned by tests with no
 * renderer. The director resolves each anchor to a world point at spawn time.
 */

import type { AbilityFeel, GameFeedbackEvent } from '../feedback/feedbackBus.js';
import type { VfxPresetId } from './presets.js';

/** Where a burst plays. `self` is the own avatar; `entity` a wire entity's drawn body. */
export type VfxAnchor =
  | { at: 'self' }
  | { at: 'entity'; id: number }
  | { at: 'node'; id: number }
  | { at: 'point'; x: number; z: number };

export interface VfxSpawn {
  preset: VfxPresetId;
  anchor: VfxAnchor;
  /** Size/speed scale (a slam's radius); default 1. */
  scale?: number;
}

/** Screen-space feedback that is not a particle (the DOM damage vignette). */
export type ScreenCue = 'hurt' | null;

/**
 * Each ability's presentation family — FRONTEND-LOCAL presentation data, like
 * `ABILITY_MOTION` (abilities are not in the client pack). Pinned against
 * `content/progression/abilities` by `cues.test.ts`: an ability with no row FAILS the
 * suite, and each row must agree with the ability's effect kinds.
 */
export const ABILITY_VFX: Readonly<Record<string, AbilityFeel>> = {
  bandage: 'heal',
  'mending-touch': 'heal',
  'staff-mending-wave': 'heal',
  'iron-skin': 'shield',
  'shield-bulwark-stance': 'shield',
  'mirror-image': 'decoy',
  'guided-insight': 'buff',
  'rallying-cry': 'buff',
  'ward-pulse': 'buff',
  'war-shout': 'control',
  'dodge-roll': 'dash',
  'bow-piercing-shot': 'strike',
  'bow-volley': 'strike',
  'calculated-strike': 'strike',
  'charging-strike': 'strike',
  'enervating-bolt': 'strike',
  'ground-pound': 'strike',
  'quick-strike': 'strike',
  'shield-bash': 'strike',
  'spark-surge': 'strike',
  'staff-arcane-bolt': 'strike',
  'sword-cleave': 'strike',
  'sword-riposte': 'strike',
};

export function abilityFeel(abilityId: string): AbilityFeel | null {
  return Object.prototype.hasOwnProperty.call(ABILITY_VFX, abilityId)
    ? (ABILITY_VFX[abilityId] ?? null)
    : null;
}

/** A gathering node's family, read from its resource id (content ids name their stuff). */
export type NodeFamily = 'ore' | 'wood' | 'herb' | 'fibre' | 'other';

/** Keyword → family, first match wins. Data: a new resource family is a new row. */
export const NODE_FAMILY_KEYWORDS: readonly (readonly [RegExp, NodeFamily])[] = [
  [/ore|stone|vein|rock|crystal|shard|flint|quartz|copper|iron|tin|silver|gold|coal|clay/, 'ore'],
  [/bark|wood|log|timber|branch|root|resin|sap/, 'wood'],
  [/herb|leaf|moss|flower|bloom|petal|mushroom|cap|spore|berry|seed|weed|reed|thistle/, 'herb'],
  [/fibre|fiber|cotton|flax|silk|wool|hide|pelt|thread|grass/, 'fibre'],
];

export function nodeFamily(resource: string): NodeFamily {
  const id = resource.toLowerCase();
  for (const [re, fam] of NODE_FAMILY_KEYWORDS) if (re.test(id)) return fam;
  return 'other';
}

const STRIKE_BY_FAMILY: Readonly<Record<NodeFamily, VfxPresetId>> = {
  ore: 'strikeOre',
  wood: 'strikeWood',
  herb: 'strikeHerb',
  fibre: 'strikeFibre',
  other: 'strikeDust',
};

function abilitySpawns(feel: AbilityFeel | null, sourceId: number): VfxSpawn[] {
  const caster: VfxAnchor = { at: 'entity', id: sourceId };
  switch (feel) {
    case 'heal':
      return [
        { preset: 'healGlow', anchor: caster },
        { preset: 'healMotes', anchor: caster },
      ];
    case 'shield':
      return [
        { preset: 'shieldGlow', anchor: caster },
        { preset: 'shieldShimmer', anchor: caster },
      ];
    case 'decoy':
      return [
        { preset: 'decoyGlow', anchor: caster },
        { preset: 'decoyShimmer', anchor: caster },
      ];
    case 'buff':
      return [{ preset: 'buffMotes', anchor: caster }];
    case 'control':
      return [{ preset: 'controlRing', anchor: caster }];
    case 'dash':
      return [{ preset: 'dodgeKick', anchor: caster }];
    // A strike's blow is cued by its `weaponHit` (the target), not the cast.
    default:
      return [];
  }
}

/** The bursts one feedback event plays. */
export function cueSpawns(event: GameFeedbackEvent): VfxSpawn[] {
  switch (event.kind) {
    case 'weaponHit': {
      const target: VfxAnchor = { at: 'entity', id: event.targetId };
      return event.heavy
        ? [
            { preset: 'heavyFlash', anchor: target },
            { preset: 'heavySpark', anchor: target },
          ]
        : [
            { preset: 'hitFlash', anchor: target },
            { preset: 'hitSpark', anchor: target },
          ];
    }
    case 'playerDamaged':
      return [{ preset: 'hurtSpark', anchor: { at: 'self' } }];
    case 'ability':
      return abilitySpawns(event.feel, event.sourceId);
    case 'dodge':
      return [{ preset: 'dodgeKick', anchor: { at: 'self' } }];
    case 'telegraphStart':
      return [
        {
          preset: 'windupPulse',
          anchor: { at: 'point', x: event.x, z: event.z },
          scale: Math.max(1, event.radius * 2),
        },
      ];
    case 'telegraphLand': {
      const at: VfxAnchor = { at: 'point', x: event.x, z: event.z };
      const r = Math.max(1, event.radius);
      return [
        { preset: 'slamWave', anchor: at, scale: r },
        { preset: 'slamDust', anchor: at, scale: Math.min(2, Math.sqrt(r)) },
        { preset: 'slamSpark', anchor: at },
      ];
    }
    case 'gatherStrike':
      return [
        {
          preset: STRIKE_BY_FAMILY[nodeFamily(event.resource)],
          anchor: { at: 'node', id: event.nodeId },
        },
      ];
    case 'gather':
      if (!event.ok) return [];
      return [
        {
          preset: STRIKE_BY_FAMILY[nodeFamily(event.resource)],
          anchor: { at: 'node', id: event.nodeId },
        },
        { preset: 'gatherSparkle', anchor: { at: 'node', id: event.nodeId } },
      ];
    case 'craft':
      return event.ok
        ? [
            { preset: 'craftGlow', anchor: { at: 'self' } },
            { preset: 'craftSparkle', anchor: { at: 'self' } },
          ]
        : [];
    case 'loot': {
      const body: VfxAnchor = { at: 'entity', id: event.entityId };
      return event.rare
        ? [
            { preset: 'lootRare', anchor: body },
            { preset: 'lootBeam', anchor: body },
          ]
        : [{ preset: 'lootBeam', anchor: body }];
    }
    case 'objectiveComplete':
      return [{ preset: 'questRing', anchor: { at: 'self' } }];
    case 'questReady':
    case 'questComplete':
      return [
        { preset: 'questRing', anchor: { at: 'self' } },
        { preset: 'questBurst', anchor: { at: 'self' } },
      ];
    case 'levelUp':
      return [
        { preset: 'levelRing', anchor: { at: 'self' } },
        { preset: 'levelGlow', anchor: { at: 'self' } },
        { preset: 'levelColumn', anchor: { at: 'self' } },
      ];
    case 'died':
      return [
        { preset: 'deathWisps', anchor: { at: 'entity', id: event.entityId } },
        { preset: 'deathMotes', anchor: { at: 'entity', id: event.entityId } },
      ];
    case 'respawned':
      return [
        { preset: 'respawnRing', anchor: { at: 'point', x: event.x, z: event.z } },
        { preset: 'respawnColumn', anchor: { at: 'point', x: event.x, z: event.z } },
      ];
  }
}

/** The screen-space cue one event plays (the damage vignette), if any. */
export function screenCue(event: GameFeedbackEvent): ScreenCue {
  if (event.kind === 'playerDamaged') return 'hurt';
  if (event.kind === 'telegraphLand' && event.hitSelf) return 'hurt';
  return null;
}

/**
 * The DEV gallery (`?vfxgallery=<name>`): one feedback event per named effect, so the
 * visual lane can film each effect on demand through the SAME cue path play uses.
 * `selfId`/`targetId` are the own avatar's and a nearby body's wire ids.
 */
export const VFX_GALLERY_NAMES = [
  'hit',
  'heavy-hit',
  'hurt',
  'heal',
  'shield',
  'decoy',
  'dodge',
  'gather-ore',
  'gather-wood',
  'gather-herb',
  'craft',
  'loot',
  'quest',
  'level-up',
  'telegraph',
  'slam',
  'death',
  'respawn',
] as const;
export type VfxGalleryName = (typeof VFX_GALLERY_NAMES)[number];

/**
 * How long the gallery lets an effect play before freezing it, ms — near each effect's
 * fullest moment (a hit flash is gone in ~0.15 s; a level-up column takes ~0.4 s to rise).
 */
export function galleryFreezeMs(name: string): number {
  switch (name) {
    case 'hit':
    case 'heavy-hit':
    case 'hurt':
      return 60;
    case 'dodge':
      return 110;
    case 'telegraph':
    case 'slam':
      return 180;
    case 'all':
      return 110;
    default:
      return 380;
  }
}

export function galleryEvents(
  name: string,
  ctx: { selfId: number; targetId: number; x: number; z: number; nodeId: number },
): GameFeedbackEvent[] {
  const { selfId, targetId, x, z, nodeId } = ctx;
  switch (name as VfxGalleryName) {
    case 'hit':
      return [{ kind: 'weaponHit', sourceId: selfId, targetId, heavy: false }];
    case 'heavy-hit':
      return [{ kind: 'weaponHit', sourceId: selfId, targetId, heavy: true }];
    case 'hurt':
      return [{ kind: 'playerDamaged', amount: 1 }];
    case 'heal':
      return [{ kind: 'ability', sourceId: selfId, abilityId: 'bandage', feel: 'heal' }];
    case 'shield':
      return [{ kind: 'ability', sourceId: selfId, abilityId: 'iron-skin', feel: 'shield' }];
    case 'decoy':
      return [{ kind: 'ability', sourceId: selfId, abilityId: 'mirror-image', feel: 'decoy' }];
    case 'dodge':
      return [{ kind: 'dodge', dirX: 1, dirZ: 0 }];
    case 'gather-ore':
      return [{ kind: 'gather', nodeId, resource: 'veinstone-ore', ok: true }];
    case 'gather-wood':
      return [{ kind: 'gather', nodeId, resource: 'fenroot-bark', ok: true }];
    case 'gather-herb':
      return [{ kind: 'gather', nodeId, resource: 'marsh-herb', ok: true }];
    case 'craft':
      return [{ kind: 'craft', ok: true }];
    case 'loot':
      return [{ kind: 'loot', entityId: targetId, rare: true }];
    case 'quest':
      return [{ kind: 'questComplete', name: 'gallery' }];
    case 'level-up':
      return [{ kind: 'levelUp', level: 2 }];
    case 'telegraph':
      return [{ kind: 'telegraphStart', sourceId: targetId, x, z, radius: 2.5, heavy: true }];
    case 'slam':
      return [{ kind: 'telegraphLand', sourceId: targetId, x, z, radius: 2.5, hitSelf: false }];
    case 'death':
      return [{ kind: 'died', entityId: targetId, self: false }];
    case 'respawn':
      return [{ kind: 'respawned', entityId: selfId, x, z, self: true }];
    default:
      return [];
  }
}
